import React, { useState, useEffect, useRef } from 'react';
import { createConsumer } from '@rails/actioncable';

// RN の CHAT_TEMPLATES と揃える。タイトルと firstMessage を保持。
const TEMPLATES = {
  circle_knowledge_creator: {
    title: 'サークルの魅力を教えてください',
    firstMessage:
      'こんにちは😀あなたのサークルのナレッジ登録をお手伝いします。\n' +
      'サークルの魅力や特徴をいくつかの観点からお聞きして、あなたのサークルに興味を持った方へのAI案内に活用します。\n' +
      'まずはじめに、サークルの一番の魅力や他にはない特長を教えていただけますか？',
  },
  circle_knowledge_user: {
    title: 'サークルについて質問する',
    firstMessage:
      'こんにちは😀このサークルについて気になることを聞いてください！\n' +
      '活動内容、雰囲気など、気になることがあればお気軽にきいてください。',
  },
  event_knowledge_creator: {
    title: 'イベントの魅力を教えてください',
    firstMessage:
      'こんにちは😀このイベントのナレッジ登録をお手伝いします。\n' +
      'イベントの魅力や特徴、参加者に向けた情報をいくつか伺います。まずは一番の魅力を教えてください。',
  },
  event_knowledge_user: {
    title: 'イベントについて質問する',
    firstMessage:
      'こんにちは😀このイベントについて気になることを聞いてください！\n' +
      '内容・雰囲気・参加条件など、気軽にどうぞ。',
  },
};

// RN の ChatView.js + BottomSheetChatScreen.js を Web に移植したモーダル型チャット。
// 会話は chat_conversations テーブルで永続化され、モーダル再開時に履歴を復元する。
export default function CircleChat({
  templateType,
  scopeType,
  scopeId,
  canManage = false,
  captchaToken = null,
  onClose,
}) {
  const template = TEMPLATES[templateType] || TEMPLATES.circle_knowledge_user;

  // null: 履歴取得中（スピナー表示）、配列: 表示可能
  const [messages, setMessages] = useState(null);
  const [chatConversationId, setChatConversationId] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const consumerRef = useRef(null);
  const subRef = useRef(null);
  const messageListRef = useRef(null);
  // POST成功直後に自分のユーザーメッセージを手動追加した直後、
  // サーバーのbroadcast(start)が来るまでの間、他のイベントを無視するためのフラグ。
  const isSettingPostedMessage = useRef(false);

  const buildFirstMessageArr = () =>
    template.firstMessage
      ? [
          {
            id: `first-${Date.now()}`,
            role: 'assistant',
            content: template.firstMessage,
          },
        ]
      : [];

  // 履歴取得 - mount時およびテンプレ/scope切替時に会話履歴を取得して復元する
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const params = new URLSearchParams({ template_type: templateType });
        if (scopeType === 'circle' && scopeId) {
          params.set('circle_id', String(scopeId));
        }
        if (scopeType === 'event' && scopeId) {
          params.set('event_id', String(scopeId));
        }
        const res = await fetch(
          `/api/chat_conversations/messages?${params.toString()}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          },
        );
        if (!res.ok) {
          throw new Error(`getChatMessages status=${res.status}`);
        }
        const payload = await res.json();
        if (cancelled) return;
        const { chat_conversation, chat_messages } = payload?.data ?? {};
        setChatConversationId(chat_conversation?.external_id ?? null);
        if (
          chat_conversation &&
          Array.isArray(chat_messages) &&
          chat_messages.length > 0
        ) {
          setMessages(chat_messages);
        } else {
          setMessages(buildFirstMessageArr());
        }
      } catch (e) {
        if (cancelled) return;
        // 履歴取得に失敗しても firstMessage で会話を開始できるようにする
        // eslint-disable-next-line no-console
        console.error('getChatMessages failed', e);
        setMessages(buildFirstMessageArr());
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateType, scopeType, scopeId]);

  // メッセージ追加後に末尾までスクロール
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  // ActionCable 購読 - chatConversationId が確定してから購読を開始
  useEffect(() => {
    if (!chatConversationId) return undefined;

    // Web では Devise セッション cookie で WebSocket を認証するため token 不要
    const consumer = createConsumer();
    consumerRef.current = consumer;

    // サーバの ChatChannel は params[:chat_conversation_id] を期待する (chat_channel.rb 参照)
    // Backend broadcast channel: "chat_#{chat_conversation.external_id}"
    subRef.current = consumer.subscriptions.create(
      {
        channel: 'ChatChannel',
        chat_conversation_id: chatConversationId,
      },
      {
        received(data) {
          if (isSettingPostedMessage.current) {
            return;
          }
          const type = data?.type;
          const assistantMessage = data?.assistant_message;
          if (type === 'error') {
            const content =
              data?.assistant_message?.content || 'エラーが発生しました';
            setMessages((prev) => [
              ...(prev ?? []),
              { role: 'assistant', content, isError: true },
            ]);
            setLoading(false);
            return;
          }
          if (data?.error && !type) {
            setMessages((prev) => [
              ...(prev ?? []),
              { role: 'assistant', content: data.error, isError: true },
            ]);
            setLoading(false);
            return;
          }
          if (!assistantMessage) {
            return;
          }
          if (type === 'start') {
            setMessages((prev) => [
              ...(prev ?? []),
              { ...assistantMessage, isGenerating: true },
            ]);
          } else if (type === 'delta') {
            const { id, content } = assistantMessage;
            if (id == null) return;
            setMessages((prev) => {
              if (!prev) return prev;
              if (prev.findIndex((m) => m?.id === id) === -1) {
                return [...prev, { ...assistantMessage, isGenerating: true }];
              }
              return prev.map((m) => (m?.id === id ? { ...m, content } : m));
            });
          } else if (type === 'completed') {
            const { id, content } = assistantMessage;
            if (id == null) return;
            setMessages((prev) => {
              if (!prev) return prev;
              if (prev.findIndex((m) => m?.id === id) === -1) {
                return [...prev, { ...assistantMessage, isGenerating: false }];
              }
              return prev.map((m) =>
                m?.id === id ? { ...m, content, isGenerating: false } : m,
              );
            });
            setLoading(false);
          }
        },
      },
    );

    return () => {
      if (subRef.current) {
        try {
          subRef.current.unsubscribe();
        } catch (_e) {
          // no-op
        }
      }
      if (consumerRef.current) {
        try {
          consumerRef.current.disconnect();
        } catch (_e) {
          // no-op
        }
      }
    };
  }, [chatConversationId]);

  const csrfToken = () => {
    const el = document.querySelector('meta[name="csrf-token"]');
    return el ? el.content : '';
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading || messages === null) return;

    isSettingPostedMessage.current = true;
    setLoading(true);

    const body = {
      template_type: templateType,
      scope: { type: scopeType, id: scopeId },
      mode: canManage ? 'learn' : 'user',
      // external_id は既存会話のルックアップまたは新規作成時のIDとして使う。
      // null の場合はサーバーが自動採番する。
      external_id: chatConversationId,
      question: text,
    };
    if (captchaToken) body.captcha_token = captchaToken;

    try {
      const res = await fetch('/api/chat_conversations/post_message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        isSettingPostedMessage.current = false;
        setMessages((prev) => [
          ...(prev ?? []),
          {
            role: 'assistant',
            content:
              payload.error ||
              `メッセージの送信に失敗しました (status=${res.status})`,
            isError: true,
          },
        ]);
        setLoading(false);
        return;
      }
      const { chat_conversation, chat_message } = payload?.data ?? {};
      if (!chat_conversation || !chat_message) {
        throw new Error('invalid chat response');
      }
      setChatConversationId(chat_conversation.external_id);
      setMessages((prev) => [...(prev ?? []), chat_message]);
      setInput('');
      isSettingPostedMessage.current = false;
    } catch (e) {
      isSettingPostedMessage.current = false;
      setMessages((prev) => [
        ...(prev ?? []),
        {
          role: 'assistant',
          content: 'メッセージの送信に失敗しました',
          isError: true,
        },
      ]);
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  const isValid = input.trim().length > 0 && !loading && messages !== null;

  return (
    <div
      className="cchat-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div className="cchat-modal">
        <div className="cchat-modal-header">
          <button
            type="button"
            className="cchat-close-btn"
            onClick={onClose}
            aria-label="閉じる"
          >
            ×
          </button>
          <div className="cchat-ai-icon" aria-hidden="true">
            🤖
          </div>
          <div className="cchat-header-title">
            <div className="cchat-header-label">AIアシスタント</div>
            <div className="cchat-header-title-text">『{template.title}』</div>
          </div>
        </div>

        {messages === null ? (
          <div className="cchat-loading" role="status" aria-label="読み込み中">
            <div className="cchat-spinner" />
          </div>
        ) : (
          <div className="cchat-messages" ref={messageListRef}>
            {messages.map((m, i) => (
              <div
                key={m.id || i}
                className={`cchat-msg-row cchat-msg-row-${m.role}`}
              >
                {m.role !== 'user' && (
                  <div className="cchat-msg-avatar" aria-hidden="true">
                    🤖
                  </div>
                )}
                <div
                  className={`cchat-msg-bubble${m.isError ? ' cchat-msg-bubble-error' : ''}`}
                >
                  {m.content}
                  {m.isGenerating ? ' ...🤖' : ''}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="cchat-input-area">
          <div className="cchat-input-box">
            <textarea
              className="cchat-textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder=""
              disabled={messages === null}
            />
            <button
              type="button"
              className="cchat-submit-btn"
              onClick={handleSend}
              disabled={!isValid}
              aria-label="送信"
            >
              ↑
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
